import type { STREntityResponse } from './types'

export type YearRange = [startYear: number, endYear: number] | null

export const NOT_AVAILABLE = 'N/A'

export const PLATFORM_CONFIG: Record<string, { label: string; color: string; activeBackground: string }> = {
  airbnb: { label: 'Airbnb', color: '#FF5A5F', activeBackground: '#FF5A5F1A' },
  vrbo: { label: 'VRBO', color: '#1890FF', activeBackground: '#1890FF1A' },
  booking: { label: 'Booking', color: '#003580', activeBackground: '#0035801A' },
}

export const PLATFORM_ICONS: Record<string, string> = {
  airbnb: require('components/BusinessSource/assets/original/airbnb.svg'),
  vrbo: require('./assets/vrbo.svg'),
}

export const getBusinessSourceIcon = (platform: string): string | null => {
  try {
    return require(`components/BusinessSource/assets/original/${platform}.svg`)
  } catch {
    return null
  }
}

export const ESTIMATED_REVENUE_MIN_MULTIPLIER = 0.88
export const ESTIMATED_REVENUE_MAX_MULTIPLIER = 1.15

export const decodeHtmlEntities = (value: string | undefined | null): string | undefined => {
  if (value == null) return undefined
  if (typeof document === 'undefined' || !value.includes('&')) return value
  const txt = document.createElement('textarea')
  txt.innerHTML = value
  return txt.value
}

export const scaleByYear = (
  obj: Record<string, number> | undefined,
  multiplier: number,
): Record<string, number> | undefined => {
  if (!obj) return obj
  return Object.fromEntries(Object.entries(obj).map(([year, amount]) => [year, amount * multiplier]))
}

export const sumValues = (obj: Record<string, number> | undefined): number =>
  obj ? Object.values(obj).reduce((sum, v) => sum + v, 0) : 0

export const buildOverviewChartData = (byYear: STREntityResponse['totals']['byYear']) => {
  const allYears = new Set([
    ...Object.keys(byYear.documented ?? {}),
    ...Object.keys(byYear.estimated ?? {}),
    ...Object.keys(byYear.reportedByYear ?? {}),
    ...Object.keys(byYear.expenses ?? {}),
  ])
  return [...allYears].sort().map(year => ({
    year,
    reported: byYear.reportedByYear?.[year] ?? null,
    estimated: byYear.estimated?.[year] ?? null,
    expenses: byYear.expenses?.[year] ?? null,
  }))
}

export const buildListingMarkers = (revenueSources: STREntityResponse['revenueSources']) =>
  (revenueSources ?? [])
    .filter(rs => rs.overview?.coordinates?.lat != null && rs.overview?.coordinates?.lng != null)
    .map(rs => ({
      id: rs.profileUrl,
      platform: rs.platform,
      lat: rs.overview.coordinates.lat,
      lng: rs.overview.coordinates.lng,
      title: rs.profileName,
      estimatedRevenue: rs.overview.totalRevenue ?? 0,
      images: rs.profileImageUrl ? [rs.profileImageUrl] : ([] as string[]),
    }))

export const buildListingRows = (revenueSources: STREntityResponse['revenueSources']) =>
  (revenueSources ?? []).map(rs => ({
    listingId: rs.profileUrl.split('/').pop() ?? rs.profileUrl,
    platform: rs.platform,
    url: rs.profileUrl,
    estimatedRevenue: rs.overview?.totalRevenue ?? 0,
    propertyType: rs.overview?.propertyType,
    isActive: rs.overview?.isActive ?? false,
    reviewsCount: rs.overview?.reviewsCount,
    ownerName: decodeHtmlEntities(rs.overview?.ownerName),
    jurisdiction: rs.overview?.jurisdiction,
    address: rs.overview?.address,
    licenseNum: rs.overview?.licenseNum,
  }))

const PLATFORM_BY_DOMAIN: Record<string, string> = {
  'airbnb.com': 'airbnb',
  'vrbo.com': 'vrbo',
  'linkedin.com': 'linkedin',
  'instagram.com': 'instagram',
  'facebook.com': 'facebook',
  'twitter.com': 'twitter',
  'x.com': 'twitter',
}

const extractPlatform = (url: string): string | null => {
  try {
    const hostname = new URL(url).hostname.replace('www.', '')
    for (const [domain, platform] of Object.entries(PLATFORM_BY_DOMAIN)) {
      if (hostname.endsWith(domain)) return platform
    }
  } catch {
    /* invalid url */
  }
  return null
}

export const buildHostSocials = (profileUrls: string[] | undefined) => {
  if (!profileUrls?.length) return []
  const seen = new Set<string>()
  return profileUrls
    .map(url => ({ platform: extractPlatform(url), url }))
    .filter((s): s is { platform: string; url: string } => {
      if (!s.platform || seen.has(s.platform)) return false
      seen.add(s.platform)
      return true
    })
}

export const buildReviewChartFromActivities = (
  activities: { activityDate: string; activitySource: string }[] | null,
): { month: string; [platform: string]: string | number }[] => {
  if (!activities?.length) return []
  const buckets: Record<string, Record<string, number>> = {}
  activities.forEach(a => {
    if (!a.activityDate) return
    const d = new Date(a.activityDate)
    const key = `${d.toLocaleString('en', { month: 'short' })} ${String(d.getFullYear()).slice(2)}`
    const platform = (a.activitySource || 'unknown').toLowerCase()
    if (!buckets[key]) buckets[key] = {}
    buckets[key][platform] = (buckets[key][platform] || 0) + 1
  })
  const sorted = Object.entries(buckets).sort(([a], [b]) => {
    const parse = (s: string) => {
      const [mon, yr] = s.split(' ')
      return new Date(`${mon} 20${yr}`)
    }
    return parse(a).getTime() - parse(b).getTime()
  })
  return sorted.map(([month, platforms]) => ({ month, ...platforms }))
}

export const formatCurrency = (value: number | null): string => {
  if (value === null) return NOT_AVAILABLE
  return `$${value.toLocaleString()}`
}

export const formatAbbreviatedCurrency = (value: number, decimals = 2): string => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(decimals)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(decimals)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(decimals)}K`
  return `$${value}`
}

export const formatYAxis = (value: number): string => {
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`
  return `${value}`
}

const filterRecordByYearRange = (
  obj: Record<string, number> | undefined,
  range: YearRange,
): Record<string, number> | undefined => {
  if (!range || !obj) return obj
  const [start, end] = range
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => {
      const y = parseInt(key, 10)
      return y >= start && y <= end
    }),
  )
}

export const sumValuesForRange = (obj: Record<string, number> | undefined, range: YearRange): number =>
  sumValues(filterRecordByYearRange(obj, range))

type ChartDataPoint = { year: string; reported: number | null; estimated: number | null; expenses: number | null }

export const filterChartDataByRange = (data: ChartDataPoint[], range: YearRange): ChartDataPoint[] => {
  if (!range) return data
  const [start, end] = range
  return data.filter(d => {
    const y = parseInt(d.year, 10)
    return y >= start && y <= end
  })
}

export const buildListingMarkersForRange = (revenueSources: STREntityResponse['revenueSources'], range: YearRange) =>
  (revenueSources ?? [])
    .filter(rs => rs.overview?.coordinates?.lat != null && rs.overview?.coordinates?.lng != null)
    .map(rs => ({
      id: rs.profileUrl,
      platform: rs.platform,
      lat: rs.overview.coordinates.lat,
      lng: rs.overview.coordinates.lng,
      title: rs.profileName,
      estimatedRevenue: range
        ? sumValues(filterRecordByYearRange(rs.revenueByYear?.estimated, range))
        : (rs.overview.totalRevenue ?? 0),
      images: rs.profileImageUrl ? [rs.profileImageUrl] : ([] as string[]),
    }))
